import { ArrowLeft } from "lucide-react"

import AwarenessIcon from "@/assets/img/aura/insight-awareness.svg?react"
import IntuitionIcon from "@/assets/img/aura/insight-intuition.svg?react"
import SubtletyIcon from "@/assets/img/aura/insight-subtlety.svg?react"
import RadarRingInner from "@/assets/img/aura/radar-ring-inner.svg?react"
import RadarRingOuter from "@/assets/img/aura/radar-ring-outer.svg?react"
import TopGlow from "@/assets/img/soulmate/top-glow.png"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { StepComponentProps } from "./step-contract"

type Level = "low" | "medium" | "high"

const LEVEL_FROM_ANSWER: Record<string, Level> = {
  never: "low",
  sometimes: "medium",
  often: "high",
}

const LEVEL_FILL: Record<Level, number> = {
  low: 0.12,
  medium: 0.5,
  high: 1,
}

const LEVEL_LABEL: Record<Level, string> = {
  low: "LOW",
  medium: "MEDIUM",
  high: "HIGH",
}

function levelFrom(answers: Record<string, string | string[]>, key: string): Level {
  const value = answers[key]
  if (typeof value !== "string") return "medium"
  return LEVEL_FROM_ANSWER[value] ?? "medium"
}

const GAUGE_PATH =
  "M10.53 67.5C8.257 62.121 7 56.207 7 50C7 25.147 27.147 5 52 5C76.853 5 97 25.147 97 50C97 56.207 95.743 62.121 93.47 67.5"

function Gauge({
  colorClass,
  fill,
}: {
  colorClass: string
  fill: number
}) {
  return (
    <svg viewBox="0 0 104 72" className="-translate-y-[2px] h-[72px] w-[104px]" aria-hidden>
      <path
        d={GAUGE_PATH}
        fill="none"
        stroke="rgba(223,203,245,0.2)"
        strokeWidth="9"
        strokeLinecap="round"
      />
      <path
        d={GAUGE_PATH}
        fill="none"
        className={colorClass}
        stroke="currentColor"
        strokeWidth="9"
        strokeLinecap="round"
        pathLength={100}
        strokeDasharray={`${fill * 100} 100`}
      />
    </svg>
  )
}

/**
 * Sensitivity radar (Step19.4): three semicircle gauges derived from the
 * nature / weather / signs frequency answers (never=low, sometimes=medium,
 * often=high). Step19 (dreams) does not feed the radar.
 */
export function AuraRadarStep({ step, onAdvance, onBack, answers }: StepComponentProps) {
  const nature = levelFrom(answers, "nature-freq")
  const energy = levelFrom(answers, "weather-freq")
  const intuition = levelFrom(answers, "signs-freq")

  const axes = [
    {
      id: "nature",
      label: "Nature",
      level: nature,
      description: "Your connection to nature’s restoring energy",
      Icon: SubtletyIcon,
      iconClass: "",
      tone: "text-green",
      chip: "bg-green/20 text-green",
      className: "left-[107px] top-0",
    },
    {
      id: "intuition",
      label: "Intuition",
      level: intuition,
      description: "Your sensitivity to signs and patterns",
      Icon: IntuitionIcon,
      iconClass: "-scale-y-100",
      tone: "text-main-purple",
      chip: "bg-main-purple/20 text-main-purple",
      className: "left-0 top-[182px]",
    },
    {
      id: "energy",
      label: "Energy",
      level: energy,
      description: "Your sensitivity to energy shifts around you",
      Icon: AwarenessIcon,
      iconClass: "",
      tone: "text-gold",
      chip: "bg-gold/20 text-gold",
      className: "left-[214px] top-[182px]",
    },
  ] as const

  return (
    <div
      className="relative flex h-dvh min-h-dvh flex-col overflow-hidden bg-dark-base text-purple-light"
      data-slot="aura-radar-step"
    >
      <img
        src={TopGlow}
        alt=""
        className="pointer-events-none absolute inset-x-0 top-0 h-[240px] w-full object-cover"
      />

      <div className="relative flex flex-1 flex-col">
        <div className="flex h-14 items-center px-4">
          <button
            type="button"
            onClick={onBack}
            aria-label="Go back"
            className="-ml-2 flex size-10 items-center justify-center rounded-full text-purple-light transition-colors hover:bg-purple-light/10"
          >
            <ArrowLeft className="size-6" />
          </button>
        </div>

        <div className="space-y-2 px-4 text-center">
          <h1 className="font-display text-2xl leading-8 font-medium text-purple-light">{step.title}</h1>
          {step.subtitle && <p className="text-base text-purple-light/70">{step.subtitle}</p>}
        </div>

        <div className="flex flex-1 items-center justify-center px-4">
          <div className="relative h-[356px] w-[354px]">
            <RadarRingOuter
              aria-hidden
              className="pointer-events-none absolute top-[26px] left-1/2 h-[223px] w-[302px] -translate-x-1/2"
            />
            <RadarRingInner
              aria-hidden
              className="pointer-events-none absolute top-[43px] left-1/2 h-[196px] w-[266px] -translate-x-1/2"
            />
            {axes.map((axis) => (
              <div
                key={axis.id}
                className={cn("absolute flex w-[140px] flex-col items-center gap-2", axis.className)}
              >
                <div className={cn("relative", axis.tone)}>
                  <Gauge colorClass={axis.tone} fill={LEVEL_FILL[axis.level]} />
                  <axis.Icon
                    className={cn(
                      "absolute top-[25px] left-1/2 size-10 -translate-x-1/2",
                      axis.iconClass,
                    )}
                  />
                </div>
                <span
                  className={cn(
                    "flex w-[106px] items-center justify-center rounded-2xl px-2 py-1.5 text-xs font-medium uppercase",
                    axis.chip,
                  )}
                >
                  {axis.label}
                </span>
                <div className="flex flex-col items-center gap-0.5 text-center">
                  <p className="font-display text-xl font-medium text-purple-light">
                    {LEVEL_LABEL[axis.level]}
                  </p>
                  <p className="text-xs font-medium text-purple-light/70">{axis.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="px-4 pb-safe">
          <Button variant="gradient" size="xl" onClick={onAdvance}>
            {step.cta ?? "Continue"}
          </Button>
        </div>
      </div>
    </div>
  )
}
